import Component from '@glimmer/component'
import { service } from '@ember/service'
import { action } from '@ember/object'
import type { IntlService } from 'ember-intl'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { optionTemplate, optionTemplateSubLevel } from 'teacher-ui-client/utils/dropdown-templates'
import type Teacher from 'teacher-ui-client/models/teacher'
import { FormComponentsFormSelect } from 'teacher-ui-client/components/form-components/form-select'
import { t } from 'ember-intl'
import { hasId } from 'teacher-ui-client/utils/type-guards'

export type GroupFilter = SchoolClass[]

export interface GroupFilterOption {
  uid: string
  text: string
  value?: GroupFilterOption[]
  filter: GroupFilter
  [key: string]: unknown
}

interface Args {
  isSubco?: boolean
  /**
   * all school classes (regardless of subco/teacher) but should be filtered by subscription type.
   */
  allSchoolClasses?: SchoolClass[]
  /**
   * school classes the current teacher belongs to, filtered by subscription type.
   */
  mySchoolClasses?: SchoolClass[]
  groupFilterOption?: Nullable<GroupFilterOption>
  setGroupFilterOption?: (groupFilterOption: GroupFilterOption) => void
}

interface Signature {
  Args: Args
  Element: HTMLDivElement
}

export class ProgressTableGroupFilter extends Component<Signature> {
  @service intl!: IntlService

  /**
   * School classes accessible to this teacher
   */
  get schoolClasses() {
    const schoolClasses = this.args.isSubco ? this.args.allSchoolClasses : this.args.mySchoolClasses
    return schoolClasses ?? []
  }

  /**
   * The options for the school classes that a teacher can filter by when viewing the table of students.
   */
  get groupFilterOptions(): GroupFilterOption[] {
    if (this.args.isSubco) {
      // get list of unique teachers
      const teachers: Set<Teacher> = new Set([...this.schoolClasses.flatMap((sc) => sc.teachers)])
      // for each teacher, create a teacher option containing school class options, with counts
      return Array.from(teachers)
        .filter(hasId)
        .map((teacher) => {
          const schoolClassesForTeacher = this.schoolClasses.filter((sc) => sc.teachers.includes(teacher))
          const schoolClassOptions = schoolClassesForTeacher.map((sc) => ({
            text: sc.name,
            uid: `${teacher.id}-${sc.id}`,
            filter: [sc],
            studentCount: sc.students.length,
          }))
          const students = new Set([...schoolClassesForTeacher.flatMap((sc) => sc.students)])
          return {
            text: teacher.fullName,
            uid: teacher.id,
            value: schoolClassOptions,
            filter: schoolClassesForTeacher,
            studentCount: students.size,
          }
        })
        .sort((a, b) => a.text.localeCompare(b.text))
    } else {
      return this.schoolClasses
        .filter(hasId)
        .map((sc) => ({
          text: sc.name,
          uid: sc.id,
          filter: [sc],
          studentCount: sc.students.length,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    }
  }

  get allStudentsOption(): GroupFilterOption {
    const schoolClasses = this.args.allSchoolClasses ?? []
    const students = new Set([...schoolClasses.flatMap((sc) => sc.students)])
    return {
      text: this.intl.t('manage.manageProgress.controls.dropdownAllStudents'),
      filter: schoolClasses,
      studentCount: students.size,
      uid: 'all-school-students',
    }
  }

  get myStudentsOption(): GroupFilterOption {
    const schoolClasses = this.args.mySchoolClasses ?? []
    const students = new Set([...schoolClasses.flatMap((sc) => sc.students)])
    return {
      text: this.intl.t('manage.manageProgress.controls.dropdownMyStudents'),
      filter: this.args.mySchoolClasses ?? [],
      studentCount: students.size,
      uid: 'my-students',
    }
  }

  get beforeOptions() {
    return this.args.isSubco ? [this.allStudentsOption, this.myStudentsOption] : [this.myStudentsOption]
  }

  groupFilterDropdownOptionTemplate = optionTemplate

  groupFilterDropdownSubOptionTemplate = optionTemplateSubLevel

  get groupFilterOption() {
    return this.args.groupFilterOption ? this.args.groupFilterOption : this.beforeOptions[0]
  }

  /**
   * Filtering - ensure that after any filter, the page is reset to 1 (to guard against scenario where user is on
   * page 5 then filters down to a single page of results)
   */
  @action
  setGroupFilterOption(groupFilterOption: GroupFilterOption) {
    this.args.setGroupFilterOption?.(groupFilterOption)
  }

  @action
  resetGroupFilterOption() {
    this.args.setGroupFilterOption?.(this.beforeOptions[0])
  }

  <template>
    <FormComponentsFormSelect
      class="font-normal"
      data-test-group-filter
      @optionClick={{this.setGroupFilterOption}}
      @optionTemplate={{this.groupFilterDropdownOptionTemplate}}
      @optionTemplateSubLevel={{this.groupFilterDropdownSubOptionTemplate}}
      @value={{this.groupFilterOption}}
      @staticBeforeOptions={{this.beforeOptions}}
      @options={{this.groupFilterOptions}}
      @search={{@isSubco}}
      @searchPlaceholder={{t "manage.manageProgress.controls.dropdownSearchPlaceholder"}}
      @resetAction={{this.resetGroupFilterOption}}
      ...attributes
    />
  </template>
}

export default ProgressTableGroupFilter
